import React from 'react';
import { Chip, Typography } from '@mui/material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { EqualisationSettingsFormValues } from './FormWrapper';
import EqualisationInterestFeeTemplateFields from './EqualisationInterestFeeTemplateFields';
import { Column, Row } from 'further-ui/components/Layout';
import Button from 'components/Button';
import {
  PreferredReturnAccrualMethod,
  PreferredReturnBasis,
  PreferredReturnCompoundingPeriod,
} from 'further-types/lp/carry-settings';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'further-ui/components/Accordion';
import PageSection from 'components/new/PageSection';

const EqualisationInterestFeeTemplates: React.FC = () => {
  const { control, getValues } =
    useFormContext<EqualisationSettingsFormValues>();

  const equalisationInterestFeeTemplates = getValues(
    'equalisationInterestFeeTemplates',
  );

  const templateIdsInUse =
    equalisationInterestFeeTemplates
      ?.filter((template) => template.isInUse)
      ?.map((template) => template._id) ?? [];

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'equalisationInterestFeeTemplates',
  });

  const liveTemplates = useWatch({
    control,
    name: 'equalisationInterestFeeTemplates',
  });

  const handleRemoveTemplate = (index: number) => {
    if (fields.length < 2) return;

    remove(index);
  };

  return (
    <Column spacing="lg">
      <PageSection
        spacing="lg"
        withoutBottomDivider
        title={
          <Row
            justify="space-between"
            centered
            fullWidth
            breakpoint="md"
            spacing="sm"
          >
            <Column spacing="xs">
              <Typography variant="inherit">
                Equalisation interest templates
              </Typography>
              <Typography variant="body2">
                Create and manage equalisation templates that define how
                equalisation interest is calculated.
              </Typography>
            </Column>
            <Row>
              <Button
                variant="contained"
                size="large"
                type="button"
                onClick={() =>
                  append({
                    name: '',
                    equalisationInterestRate: 0,
                    equalisationInterestAccrualMethod:
                      PreferredReturnAccrualMethod.Simple,
                    equalisationInterestCompoundingPeriod:
                      PreferredReturnCompoundingPeriod.Daily,
                    equalisationInterestBasis: PreferredReturnBasis.Drawn,
                    isDefault: false,
                    isInUse: false,
                  })
                }
              >
                Add new template
              </Button>
            </Row>
          </Row>
        }
      >
        {fields.map((field, index) => (
          <Accordion key={field.id} defaultExpanded>
            <AccordionSummary>
              <Row centered fullWidth justify="space-between" spacing="sm">
                <Typography variant="h5" width="100%">
                  {liveTemplates?.[index]?.name ||
                    `Equalisation interest fee template ${index + 1}`}
                </Typography>
                {liveTemplates?.[index]?.isDefault && (
                  <Chip label="Default template" size="small" />
                )}
              </Row>
            </AccordionSummary>
            <AccordionDetails>
              <Column spacing="lg">
                <EqualisationInterestFeeTemplateFields index={index} />
                <Row>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleRemoveTemplate(index)}
                    disabled={
                      fields.length < 2 ||
                      (!!field._id && templateIdsInUse.includes(field._id))
                    }
                    tooltip={
                      !!field._id && templateIdsInUse.includes(field._id)
                        ? 'This template is in use and cannot be removed'
                        : undefined
                    }
                  >
                    Delete fee template
                  </Button>
                </Row>
              </Column>
            </AccordionDetails>
          </Accordion>
        ))}
      </PageSection>
    </Column>
  );
};

export default EqualisationInterestFeeTemplates;
